/**
 * 手柄变换的指针交互：拖动移动 / 8 手柄缩放 / 旋转。
 *
 * 分工：本文件只做「事件 → 会话 → 预览 → 提交」，坐标数学与状态机在
 * `render/transform-session.ts`（可单测）；文档改动一律经 History。
 * 屏幕坐标 → 用户坐标由 deps 注入，因此这段逻辑不依赖真实布局，jsdom 也能测。
 */
import type { Mat, Pt, Rect } from '@/core/geom/matrix';
import type { HandleId } from '@/core/geom/handles';
import type { Command } from '@/core/history';
import {
  applyPreview,
  beginSession,
  cancelSession,
  commitSession,
  sessionWorld,
  type TransformSession,
} from '../transform-session';

export type HandleTarget = HandleId | 'rotate';

export interface TransformInteractionDeps {
  host: HTMLElement;
  getSvg(): SVGSVGElement | null;
  getSelection(): readonly Element[];
  /** 选择框（根用户坐标）；为空表示不可变换 */
  getBox(): Rect | null;
  setSelection(els: Element[]): void;
  /** 屏幕 → 根用户坐标（生产用 screenToRoot；测试注入恒等映射） */
  toRoot(clientX: number, clientY: number): Pt | null;
  parentMatOf(el: Element): Mat;
  execute(cmd: Command): boolean;
  syncOverlay(): void;
  grid(): number;
  /** 命中可选中元素（与选择交互共用同一套语义） */
  resolveTarget(target: EventTarget | null): Element | null;
  /**
   * 位置锁：这个元素的位置能不能改（可选 —— 不传视为可以）。
   * 手势一动就是**整个选中集**，所以任一元素返回 false 就整体不启动。
   */
  canTransform?(el: Element): boolean;
  /** 被位置锁拦下（写状态栏说明原因） */
  onRefuse?(): void;
  /** 正在用绘制工具（此时不做移动/缩放/旋转） */
  isDrawing?(): boolean;
}

const handleOf = (target: EventTarget | null): HandleTarget | null => {
  const el = (target as Element | null)?.closest?.('[data-handle], [data-rotate]') as HTMLElement | null;
  if (!el) return null;
  if (el.dataset.rotate) return 'rotate';
  return (el.dataset.handle as HandleId | undefined) ?? null;
};

/** 指针捕获失败（pointerId 失效）不得影响提交：忽略即可 */
function capture(host: HTMLElement, id: number | null | undefined): void {
  try {
    if (id !== null && id !== undefined) host.setPointerCapture?.(id);
  } catch {
    /* 忽略 */
  }
}

function release(host: HTMLElement, id: number | null | undefined): void {
  try {
    if (id !== null && id !== undefined) host.releasePointerCapture?.(id);
  } catch {
    /* 忽略 */
  }
}

export function attachTransformInteraction(deps: TransformInteractionDeps): () => void {
  const { host } = deps;
  let session: TransformSession | null = null;
  let pointerId: number | null = null;
  /** 最后一帧的世界矩阵：提交它 = 提交用户看到的画面（松手瞬间修饰键松了也不跳变） */
  let lastWorld: Mat | null = null;

  const mods = (e: PointerEvent | MouseEvent): { shift: boolean; alt: boolean; grid: number } => ({
    shift: e.shiftKey,
    alt: e.altKey,
    grid: deps.grid(),
  });

  const finish = (e?: PointerEvent): void => {
    if (!session) return;
    const svg = deps.getSvg();
    const cur = e ? deps.toRoot(e.clientX, e.clientY) : null;
    const world = lastWorld ?? (cur ? sessionWorld(session, cur, e ? mods(e) : {}) : null);
    if (svg && world) {
      commitSession(session, world, { root: svg, parentMatOf: deps.parentMatOf }, deps.execute);
    } else {
      cancelSession(session);
    }
    session = null;
    lastWorld = null;
    release(host, pointerId);
    pointerId = null;
    deps.syncOverlay();
  };

  const onDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    if (deps.isDrawing?.()) return;
    const svg = deps.getSvg();
    if (!svg) return;
    const start = deps.toRoot(e.clientX, e.clientY);
    if (!start) return;
    const box = deps.getBox();
    if (!box) return;

    const allowed = (els: readonly Element[]): boolean =>
      els.length > 0 && els.every((el) => deps.canTransform?.(el) ?? true);

    const handle = handleOf(e.target);
    if (handle === 'rotate') {
      // 手柄动的是**整个选中集**：有一个不可动就整体不启动（只动一半会让旋转锚点错位）
      if (!allowed(deps.getSelection())) {
        deps.onRefuse?.();
        return;
      }
      session = beginSession('rotate', deps.getSelection(), box, start, deps.parentMatOf);
    } else if (handle) {
      if (!allowed(deps.getSelection())) {
        deps.onRefuse?.();
        return;
      }
      session = beginSession('resize', deps.getSelection(), box, start, deps.parentMatOf, handle);
    } else {
      const hit = deps.resolveTarget(e.target);
      if (!hit) return; // 空白 → 交给框选
      // 位置锁拦住时不启动移动：**直接返回**，让选择交互去把元素选中
      //（要素：点得中、看得见属性，就是位置不动）
      const next = deps.getSelection().includes(hit) ? deps.getSelection() : [hit];
      if (!allowed(next)) {
        deps.onRefuse?.();
        return;
      }
      if (!deps.getSelection().includes(hit)) deps.setSelection([hit]);
      session = beginSession('move', deps.getSelection(), box, start, deps.parentMatOf);
    }

    pointerId = e.pointerId ?? null;
    capture(host, pointerId);
    e.preventDefault();
  };

  const onMove = (e: PointerEvent): void => {
    if (!session) return;
    const cur = deps.toRoot(e.clientX, e.clientY);
    if (!cur) return;
    lastWorld = sessionWorld(session, cur, mods(e));
    applyPreview(session.plans, lastWorld);
    deps.syncOverlay();
    e.preventDefault();
  };

  const onUp = (e: PointerEvent): void => finish(e);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !session) return;
    cancelSession(session);
    session = null;
    deps.syncOverlay();
  };

  // 捕获阶段：手柄与元素拖动优先于「框选/点选」
  host.addEventListener('pointerdown', onDown, true);
  host.addEventListener('pointermove', onMove, true);
  host.addEventListener('pointerup', onUp, true);
  host.addEventListener('pointercancel', onUp, true);
  document.addEventListener('keydown', onKey);

  return () => {
    host.removeEventListener('pointerdown', onDown, true);
    host.removeEventListener('pointermove', onMove, true);
    host.removeEventListener('pointerup', onUp, true);
    host.removeEventListener('pointercancel', onUp, true);
    document.removeEventListener('keydown', onKey);
  };
}
